/**
 * Supabase-Based Traffic Control
 *
 * This module handles widget traffic allocation through Supabase configuration:
 * - traffic_allocated from unified config system (0-100)
 * - Deterministic bucketing based on stable client ID
 * - Decision always computed fresh from config on every page load
 * - localStorage write-only for debugging (never read back for decisions)
 *
 * How it works:
 * 1. Read traffic_allocated from site config (0-100)
 * 2. Get stable client ID (PostHog distinct_id or localStorage UUID)
 * 3. Hash client ID to assign user to bucket 0-99
 * 4. User is enabled if their bucket < traffic_allocated
 * 5. Write decision to localStorage for debugging visibility
 *
 * Traffic allocation behavior:
 * - 0 = never show widget
 * - 100 = always show widget
 * - 1-99 = deterministic probability (e.g., 20 = users in buckets 0-19 see widget)
 *
 * Key benefits:
 * - Same user gets same result across sessions/browsers (if using PostHog ID)
 * - Increasing traffic (20% → 80%) keeps original 20% enabled
 * - No async waiting for PostHog flags
 * - Fast, database-controlled rollout
 * - Changing traffic allocation takes effect immediately on next page load
 */

import type { SiteConfig } from '../../config/types';
import { roseStorage, type TrafficControlData } from '../../services/rose-storage';
import { logger } from '../core/logger';
import { maskId } from '../core/mask';
import { getStableClientId } from '../domain/clientId';

export interface TrafficControlResult {
    enabled: boolean;
    reason?: string;
    bucket?: number;
    clientId?: string;
}

/**
 * Simple hash function to map a string to a number in range [0, max)
 * Uses a variation of the FNV-1a hash algorithm
 */
function hashStringToNumber(str: string, max: number): number {
    let hash = 2166136261; // FNV offset basis

    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash = Math.imul(hash, 16777619); // FNV prime
    }

    // Convert to positive number and map to range [0, max)
    return Math.abs(hash) % max;
}

/**
 * Assign user to a bucket based on their client ID
 * @param clientId Stable client identifier
 * @returns Bucket number (0-99)
 */
function assignBucket(clientId: string): number {
    return hashStringToNumber(clientId, 100);
}

/**
 * Write traffic decision to roseStorage for debugging visibility.
 * This is write-only — never read back to influence the decision.
 */
function saveDecisionForDebugging(decision: TrafficControlData): void {
    roseStorage.setTrafficControl(decision);
}

/**
 * Check if the widget should be displayed based on traffic allocation configuration
 *
 * This function implements gradual widget rollout by:
 * 1. Reading traffic_allocated value from site config (0-100)
 * 2. Generating stable client ID bucket (0-99)
 * 3. Comparing bucket against allocation threshold
 *
 * @param siteConfig Site configuration from Supabase
 * @param posthog Optional PostHog instance for stable client ID
 * @returns Traffic control result with decision and metadata
 */
export function checkTrafficAllocation(
    siteConfig: SiteConfig | null,
    posthog?: unknown
): TrafficControlResult {
    // Check for manual override (set via browser console for testing/debugging)
    const override = roseStorage.getTrafficControlOverride();
    if (override !== null) {
        logger.info(`🔧 Traffic control override active: ${override ? 'enabled' : 'disabled'}`);
        return {
            enabled: override,
            reason: `Manual override via localStorage (${override ? 'force show' : 'force hide'})`,
        };
    }

    // Default to 100% if no config or no traffic_allocated value
    const trafficAllocated = siteConfig?.traffic_allocated ?? 100;

    logger.debug('🚦 Traffic control - checking Supabase allocation', {
        trafficAllocated,
        domain: siteConfig?.domain,
    });

    // Get stable client ID (always needed for bucket calculation)
    const clientId = getStableClientId(posthog);

    logger.debug('🆔 Using client ID for traffic control:', maskId(clientId));

    // ALWAYS compute bucket for analytics purposes, even for edge cases
    const bucket = assignBucket(clientId);

    // Compute traffic decision
    let enabled: boolean;
    let reason: string;

    if (trafficAllocated === 0) {
        enabled = false;
        reason = 'Traffic allocation is 0% (disabled)';
    } else if (trafficAllocated === 100) {
        enabled = true;
        reason = 'Traffic allocation is 100% (always enabled)';
    } else if (trafficAllocated < 0 || trafficAllocated > 100) {
        logger.warn('⚠️ Invalid traffic_allocated value:', trafficAllocated);
        enabled = true;
        reason = 'Invalid traffic_allocated value, defaulting to enabled';
    } else {
        enabled = bucket < trafficAllocated;
        reason = enabled
            ? `User in bucket ${bucket} (< ${trafficAllocated}%)`
            : `User in bucket ${bucket} (≥ ${trafficAllocated}%)`;
    }

    logger.debug('🎲 Traffic decision:', {
        clientId: maskId(clientId),
        bucket,
        trafficAllocated,
        enabled,
    });

    // Write to localStorage for debugging visibility (never read back for decisions)
    saveDecisionForDebugging({
        clientId,
        trafficAllocated,
        enabled,
        bucket,
        timestamp: Date.now(),
    });

    return { enabled, reason, bucket, clientId };
}

/**
 * Clear cached traffic decision (useful for testing or manual override)
 */
export function clearTrafficCache(): void {
    roseStorage.clearTrafficControl();
    logger.debug('🗑️ Cleared traffic decision cache');
}
